/**
 * OTC 挂单记录（admin view of GET /api/admin/otc/orders）。
 *
 * 替代旧 EasyAdmin 后台的 uo_bonus_log 页面。支持按 side/status/user_id
 * 过滤、分页、强制取消（需带原因）。
 */
import { useState } from 'react';
import { Button, Input, Modal, Select, Space, Tag, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface OtcOrderRow {
  id: string;
  user_id: number;
  side: 'buy' | 'sell';
  amount_uo: string;
  unit_price: string;
  amount_ap: string;
  filled_uo: string;
  filled_ap: string;
  fee_ep: string;
  status: string;
  cancel_reason: string | null;
  create_time: string;
  update_time: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'blue',
  partial: 'gold',
  done: 'green',
  cancelled: 'default',
};
const SIDE_COLORS: Record<string, string> = {
  buy: 'green',
  sell: 'red',
};

export function OtcOrdersPage() {
  const [side, setSide] = useState<'buy' | 'sell' | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [userId, setUserId] = useState<string>('');
  const [cancelRow, setCancelRow] = useState<OtcOrderRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCancel = hasNode('otc.order.cancel');

  async function submitForceCancel() {
    if (!cancelRow) return;
    const reason = cancelReason.trim();
    if (reason.length < 1) {
      message.warning('请填写取消原因');
      return;
    }
    try {
      await api.post('/api/admin/otc/orders/cancel', {
        order_id: Number(cancelRow.id),
        reason,
      });
      message.success(`已强制取消订单 #${cancelRow.id}`);
      setCancelRow(null);
      setCancelReason('');
      qc.invalidateQueries({ queryKey: ['admin', 'otc-orders'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '取消失败');
    }
  }

  const columns = [
    { title: '订单 ID', dataIndex: 'id', width: 110 },
    { title: '会员', dataIndex: 'user_id', width: 90 },
    {
      title: '方向',
      dataIndex: 'side',
      width: 80,
      render: (v: string) => (
        <Tag color={SIDE_COLORS[v] ?? 'default'}>{v === 'buy' ? '买入' : '卖出'}</Tag>
      ),
    },
    { title: '数量 (ORB)', dataIndex: 'amount_uo', width: 130 },
    { title: '单价 (AP)', dataIndex: 'unit_price', width: 110 },
    { title: '总额 (AP)', dataIndex: 'amount_ap', width: 130 },
    { title: '已成交 ORB', dataIndex: 'filled_uo', width: 130 },
    { title: '已成交 AP', dataIndex: 'filled_ap', width: 130 },
    { title: '手续费 (EP)', dataIndex: 'fee_ep', width: 110 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: '取消原因',
      dataIndex: 'cancel_reason',
      width: 160,
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      fixed: 'right' as const,
      render: (_: unknown, row: OtcOrderRow) => {
        const cancellable = row.status === 'open' || row.status === 'partial';
        if (!canCancel || !cancellable) return '—';
        return (
          <Button size="small" danger onClick={() => setCancelRow(row)}>
            强制取消
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>ORB 挂单记录</h2>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="方向"
          style={{ width: 120 }}
          allowClear
          value={side}
          onChange={(v) => setSide(v as 'buy' | 'sell' | undefined)}
          options={[
            { value: 'buy', label: '买入' },
            { value: 'sell', label: '卖出' },
          ]}
        />
        <Select
          placeholder="状态"
          style={{ width: 140 }}
          allowClear
          value={status}
          onChange={setStatus}
          options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))}
        />
        <Input
          placeholder="按会员 ID"
          style={{ width: 140 }}
          allowClear
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
      </Space>
      <DataTable<OtcOrderRow>
        queryKey={['admin', 'otc-orders', side ?? '', status ?? '', userId]}
        endpoint="/api/admin/otc/orders"
        extraParams={{
          side,
          status,
          user_id: userId ? Number(userId) : undefined,
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1800 }}
      />

      <Modal
        title={`强制取消订单 #${cancelRow?.id ?? ''}`}
        open={cancelRow !== null}
        onCancel={() => {
          setCancelRow(null);
          setCancelReason('');
        }}
        onOk={submitForceCancel}
        okText="提交"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p style={{ marginTop: 0 }}>
          将退回会员 {cancelRow?.user_id} 剩余未成交部分（
          {cancelRow?.side === 'buy' ? 'AP' : 'ORB'}
          ），并将订单置为 cancelled。此操作会记入审计日志。
        </p>
        <Input.TextArea
          rows={3}
          maxLength={200}
          showCount
          placeholder="请填写取消原因（1-200 字）"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
